import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';
import type { Category } from '@freelancer/datastore/collections';
import {
  flArtificialIntelligence,
  flBankV2,
  flBeaker,
  flBlocks,
  flChatSquareV2,
  flComputerOutline,
  flDevicesV2,
  flEducation,
  flFactory,
  flGraphIncrease,
  flHealthAndMedicine,
  flJobsForAnyone,
  flKeyboard,
  flPenbrush,
  flPentool,
  flTelecom,
  flTradesAndServices,
  flTruck,
  IconColor,
  IconName,
  IconSize,
  provideFlIcons,
} from '@freelancer/ui/icon';
import { ListItemPadding } from '@freelancer/ui/list-item';
import { Observable } from 'rxjs';
import { getSkillCategoryIcon } from '../skills.config';

@Component({
  standalone: false,
  selector: 'fl-skills-categories',
  template: `
    <h3
      class="text-body-16 SkillsCategoryHeading"
      i18n="Skill categories heading"
    >
      Select a category
    </h3>

    <fl-scrollable-content class="SkillsCategoryList">
      <fl-list
        [clickable]="true"
        [padding]="ListItemPadding.SMALL"
      >
        @if (categories$ | flAsync; as categories) {
          @for (
            category of categories;
            track trackByCategoryId($index, category)
          ) {
            <fl-list-item
              flTrackingLabel="SelectCategory"
              (click)="handleSelectedCategory(category)"
            >
              <div class="SkillsCategory">
                <div class="SkillsCategory-content mr-mid">
                  <fl-icon
                    class="mr-small"
                    [name]="getSkillCategoryIcon(category)"
                    [color]="IconColor.SECONDARY"
                  ></fl-icon>
                  <p>
                    {{ category.name }}
                  </p>
                </div>
                <fl-icon
                  [name]="'ui-arrow-forward'"
                  [size]="IconSize.XSMALL"
                  [useIconFont]="true"
                ></fl-icon>
              </div>
            </fl-list-item>
          }
        } @else {
          <fl-spinner
            class="SkillsCategoryList-loading"
            flTrackingLabel="SkillsCategoriesLoadingSpinner"
          ></fl-spinner>
        }
      </fl-list>
    </fl-scrollable-content>
  `,
  styleUrls: ['./skills-categories.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    provideFlIcons({
      flArtificialIntelligence,
      flBankV2,
      flBeaker,
      flBlocks,
      flChatSquareV2,
      flComputerOutline,
      flDevicesV2,
      flEducation,
      flFactory,
      flGraphIncrease,
      flHealthAndMedicine,
      flJobsForAnyone,
      flKeyboard,
      flPenbrush,
      flPentool,
      flTelecom,
      flTradesAndServices,
      flTruck,
    }),
  ],
})
export class SkillsCategoriesComponent {
  IconColor = IconColor;
  IconSize = IconSize;
  ListItemPadding = ListItemPadding;

  @Input() categories$: Observable<readonly Category[]>;
  @Output() selectCategory = new EventEmitter<Category>();

  getSkillCategoryIcon(category: Category): IconName {
    return getSkillCategoryIcon(category);
  }

  handleSelectedCategory(category: Category): void {
    this.selectCategory.emit(category);
  }

  trackByCategoryId(index: number, category: Category): number {
    return category.id;
  }
}
